<template>
  <v-dialog v-model="dialog" max-width="820" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-card-title>
        Visualize video
        <v-spacer />
        <v-btn icon aria-label="Close visualization" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <div class="visualization-video mb-5">
          <v-icon color="primary" class="mr-3">mdi-video-outline</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-medium">{{ video.name || 'Selected video' }}</div>
            <div class="text-caption">Entire video · {{ durationLabel }} · original audio included</div>
          </div>
        </div>
        <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>
        <v-progress-linear v-if="loading" indeterminate aria-label="Loading available plugins" class="mb-4" />
        <template v-if="!loading && !loadFailed">
          <h2 class="text-subtitle-1 font-weight-medium">Choose plugins</h2>
          <p class="text-body-2 text--secondary mb-2">Use the available analysis results to visualize the whole video.</p>
          <div v-if="plugins.length" class="visualization-plugins">
            <v-checkbox v-for="plugin in plugins" :key="plugin.id" v-model="selectedPlugins"
              :value="plugin.id" :label="plugin.name" hide-details class="mt-0 pa-3 visualization-plugin"
              :disabled="!!submitting || (selectedPlugins.length >= 6 && !selectedPlugins.includes(plugin.id))" />
          </div>
          <v-alert v-else type="info" outlined>
            This video has no results to visualize yet. Run analysis plugins first, then return here.
          </v-alert>
          <p v-if="plugins.length" class="text-caption text--secondary mt-2">{{ selectedPlugins.length }} selected · combine up to 6 plugins</p>
          <div class="d-flex flex-wrap mt-5 mb-4" style="gap: 12px">
            <v-btn large outlined :disabled="!canSubmit" :loading="submitting === 'preview'" @click="createExport(true)">
              <v-icon left>mdi-play-circle-outline</v-icon>Preview
            </v-btn>
            <v-btn large color="primary" :disabled="!canSubmit" :loading="submitting === 'export'" @click="createExport(false)">
              <v-icon left>mdi-download</v-icon>Export MP4
            </v-btn>
          </div>
          <p v-if="activeJobs.length >= 2" class="text-body-2">Wait for a video to finish, or cancel one below.</p>
        </template>
        <v-btn v-if="loadFailed" outlined @click="load">Retry</v-btn>
        <v-card v-for="job in activeJobs" :key="job.id" outlined class="pa-4 mb-3" aria-live="polite">
          <div class="d-flex align-center">
            <strong>{{ job.preview ? 'Preparing preview' : 'Exporting video' }}</strong>
            <v-spacer />
            <v-btn text small :disabled="job.cancel_requested" @click="cancelJob(job)">{{ job.cancel_requested ? 'Cancelling' : 'Cancel' }}</v-btn>
          </div>
          <v-progress-linear :value="job.progress * 100" :indeterminate="job.status === 'queued'" class="my-3" />
          <span class="text-caption">{{ job.status === 'queued' ? 'Waiting to start' : Math.round(job.progress * 100) + '%' }} · You can close this window while it renders.</span>
        </v-card>
        <v-alert v-for="job in failedJobs" :key="job.id" type="error" dense>{{ job.error || 'Could not render this video. Please try again.' }}</v-alert>
        <div v-if="viewedJob" class="mt-5">
          <div class="d-flex align-center mb-2">
            <strong>{{ viewedJob.preview ? 'Preview' : 'Export' }}</strong>
            <v-spacer />
            <v-btn text small :href="fileUrl(viewedJob, 'download=1')">Download MP4</v-btn>
          </div>
          <video :key="viewedJob.id" :src="fileUrl(viewedJob)" controls preload="metadata" class="visualization-preview" />
          <p class="text-caption mt-2">{{ jobLabel(viewedJob) }} · Create a new preview after changing your selection.</p>
        </div>
        <v-expansion-panels v-if="finishedJobs.length" flat class="mt-5">
          <v-expansion-panel>
            <v-expansion-panel-header>Previous previews and exports</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="job in finishedJobs" :key="job.id" class="py-3 visualization-history">
                <div>{{ job.preview ? 'Preview' : 'Export' }} · {{ new Date(job.created_at).toLocaleString() }}</div>
                <p class="text-caption mb-1">{{ jobLabel(job) }}</p>
                <v-btn small text @click="viewedJob = job">Play</v-btn>
                <v-btn small text :href="fileUrl(job, 'download=1')">Download</v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "@/plugins/axios";
import config from "../../app.config";

const endpoint = `${config.API_LOCATION}/video/visualization`;

export default {
  props: { value: Boolean, videoId: String },
  data() {
    return {
      loading: false, loadFailed: false, submitting: "", error: "", video: {}, plugins: [],
      selectedPlugins: [], jobs: [], viewedJob: null, timer: null, generation: 0,
    };
  },
  computed: {
    dialog: { get() { return this.value; }, set(value) { this.$emit("input", value); } },
    durationLabel() {
      const seconds = Math.round(this.video.duration || 0);
      return seconds >= 60 ? `${Math.floor(seconds / 60)} min ${seconds % 60} sec` : `${seconds} sec`;
    },
    activeJobs() { return this.jobs.filter(this.isActive); },
    finishedJobs() { return this.jobs.filter(job => job.status === "complete"); },
    failedJobs() { return this.jobs.filter(job => job.status === "error").slice(0, 1); },
    canSubmit() { return !this.loading && !this.loadFailed && !this.submitting && this.selectedPlugins.length > 0 && this.activeJobs.length < 2; },
  },
  watch: {
    value: { immediate: true, handler(open) {
      if (open) this.load(); else { this.generation++; this.stopPolling(); }
    } },
    videoId() {
      this.generation++; this.stopPolling();
      this.selectedPlugins = []; this.plugins = []; this.jobs = []; this.viewedJob = null; this.video = {};
      if (this.value) this.load();
    },
  },
  beforeDestroy() { this.generation++; this.stopPolling(); },
  methods: {
    isActive(job) { return ["queued", "running"].includes(job.status); },
    fileUrl(job, extra = "") { return `${endpoint}/download?id=${job.id}${extra ? `&${extra}` : ""}`; },
    jobLabel(job) { return [...new Set((job.configuration.layers || []).map(layer => layer.label))].join(" · "); },
    message(error) { return error.response?.data?.error || "Could not connect. Please try again."; },
    stopPolling() { clearTimeout(this.timer); this.timer = null; },
    async load() {
      if (!this.videoId) return;
      const generation = ++this.generation;
      this.stopPolling(); this.loading = true; this.loadFailed = false; this.error = ""; this.submitting = "";
      try {
        const { data } = await axios.get(`${endpoint}/sources`, { params: { video_id: this.videoId, simple: 1 } });
        if (generation !== this.generation) return;
        this.video = data.video; this.plugins = data.plugins; this.jobs = data.jobs;
        this.selectedPlugins = this.selectedPlugins.filter(id => this.plugins.some(plugin => plugin.id === id));
        this.viewedJob = this.jobs.find(job => job.status === "complete") || null;
        this.schedulePoll();
      } catch (error) {
        if (generation === this.generation) { this.error = this.message(error); this.loadFailed = true; }
      } finally { if (generation === this.generation) this.loading = false; }
    },
    schedulePoll() {
      this.stopPolling();
      if (this.value && this.jobs.some(this.isActive)) this.timer = setTimeout(this.poll, 1500);
    },
    async poll() {
      const generation = this.generation;
      try {
        const results = await Promise.all(this.jobs.filter(this.isActive).map(job => axios.get(`${endpoint}/status`, { params: { id: job.id } })));
        if (generation !== this.generation) return;
        results.forEach(({ data }) => {
          const index = this.jobs.findIndex(job => job.id === data.entry.id);
          if (index >= 0) this.$set(this.jobs, index, data.entry);
        });
        const latestComplete = this.jobs.find(job => job.status === "complete");
        if (latestComplete) this.viewedJob = latestComplete;
      } catch (error) { if (generation === this.generation) this.error = this.message(error); }
      finally { if (generation === this.generation) this.schedulePoll(); }
    },
    async createExport(preview) {
      if (!this.canSubmit) return;
      const generation = this.generation;
      this.error = ""; this.submitting = preview ? "preview" : "export";
      try {
        const { data } = await axios.post(`${endpoint}/create`, { video_id: this.videoId, preview, plugins: [...this.selectedPlugins] });
        if (generation !== this.generation) return;
        this.jobs.unshift(data.entry); this.viewedJob = null; this.schedulePoll();
      } catch (error) { if (generation === this.generation) this.error = this.message(error); }
      finally { if (generation === this.generation) this.submitting = ""; }
    },
    async cancelJob(job) {
      const generation = this.generation;
      try {
        const { data } = await axios.post(`${endpoint}/cancel`, { id: job.id });
        if (generation !== this.generation) return;
        const index = this.jobs.findIndex(entry => entry.id === job.id);
        if (index >= 0) this.$set(this.jobs, index, data.entry);
        this.schedulePoll();
      } catch (error) { if (generation === this.generation) this.error = this.message(error); }
    },
  },
};
</script>

<style scoped>
.visualization-preview { display: block; width: 100%; max-height: 480px; background: #111; }
.visualization-video { display: flex; align-items: center; border-radius: 8px; background: #f3f6f9; padding: 16px; color: #263747; }
.visualization-plugins { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 8px; }
.visualization-plugin { border: 1px solid #dfe5eb; border-radius: 6px; }
.visualization-history { border-bottom: 1px solid #e5e9ef; }
</style>
